import React, { useState, useEffect } from "react";
import { Table, Space, Card, Button, Select, DatePicker, Row, Col, message } from "antd";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { FileOpenOutlined, FileDownloadOutlined } from "@mui/icons-material";
import moment from "moment";
import Footer from "Common/Footer";
import ReportService from "Services/AdminServices/ReportServices";
import { ReportPeriodTypeOptions } from "Constants/Enums/ReportPeriodTypeEnum";
import jsPDF from "jspdf";
import "jspdf-autotable";

const { Option } = Select;

function EmployeeExpenseRequest() {
  const [departments, setDepartments] = useState([]);
  // "" = All Departments (report API is called without a DepartmentId). Default.
  const [selectedDepartment, setSelectedDepartment] = useState("");
  // Point 13: day/week/month period selector. periodType "" = All Time.
  const [periodType, setPeriodType] = useState("");
  const [referenceDate, setReferenceDate] = useState(null);
  const [periodLabel, setPeriodLabel] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const BASE_URL = process.env.REACT_APP_Base_URL;

  // --- Fetch Departments ---
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const res = await ReportService.getDepartments();
        const deptList = res.data || [];
        setDepartments(deptList);
      } catch (error) {
        console.error("Error fetching departments:", error);
        message.error("Failed to load departments.");
      }
    };
    fetchDepartments();
  }, []);

  // --- Fetch Expense Data when filters change ---
  useEffect(() => {
    // "" = All Departments -> pass null so the API omits DepartmentId.
    const deptId = selectedDepartment === "" ? null : selectedDepartment;
    fetchData(deptId, periodType, referenceDate);
  }, [selectedDepartment, periodType, referenceDate]);

  const fetchData = async (departmentId, pType, refDate) => {
    try {
      setLoading(true);
      const res = await ReportService.getExpenseReport(departmentId, pType, refDate);

      // Point 13: response is now { period, data }. Read the list from data.data.
      const body = res.data || {};
      const reportData = body.data;
      setPeriodLabel(body.period?.periodLabel || "");

      if (Array.isArray(reportData)) {
        setData(
          reportData.map((item) => ({
            key: item.id,
            expenseManagementId: item.id,
            employeeName: item.employee ? `${item.employee.fullName}` : "-",
            email: item.employee?.email || "-",
            date: item.date,
            description: item.description,
            charges: item.charges,
            receipt: item.receipt,
            calculationSheet: item.calculationSheet,
            status: item.status,
            approver: item.approver ? `${item.approver.fullName}` : "-",
            approvedDate: item.approvedDate,
          }))
        );
      } else {
        setData([]);
        message.warning("No expense records found.");
      }
    } catch (error) {
      console.error("Error fetching expense report:", error);
      message.error("Failed to load expense report.");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePdfPreview = (filePath) => {
    if (!filePath) {
      console.error("File path not found");
      return;
    }

    // If it's already a full URL (e.g. starts with http or https)
    const isFullUrl = /^https?:\/\//i.test(filePath);

    // Build the full URL properly
    const fullUrl = isFullUrl
      ? filePath
      : `${BASE_URL}${filePath.startsWith("/") ? "" : "/"}${filePath}`;

    // Open file in new tab
    window.open(fullUrl, "_blank", "noopener,noreferrer");
  };

  const handleDownloadPDF = () => {
    try {
      // Validate data
      if (!data || data.length === 0) {
        message.warning("No data available to generate PDF");
        return;
      }

      const doc = new jsPDF();

      // Title and Header
      doc.setFontSize(18);
      doc.setTextColor(33, 37, 41);
      doc.text("EMPLOYEE EXPENSE REPORT", 105, 20, { align: "center" });

      // Report Information
      doc.setFontSize(10);
      doc.setTextColor(108, 117, 125);

      let currentY = 35;

      // Department
      const departmentName =
        departments.find((d) => d.id === selectedDepartment)?.name || "All Departments";
      doc.text(`Department: ${departmentName}`, 14, currentY);
      currentY += 6;

      // Date Range (Point 13: use the backend-resolved period label)
      if (periodLabel) {
        doc.text(`Period: ${periodLabel}`, 14, currentY);
        currentY += 6;
      }

      // Generation Date
      doc.text(
        `Generated: ${new Date().toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })}`,
        14,
        currentY
      );
      currentY += 6;

      // Record Count
      doc.text(`Records: ${data.length}`, 14, currentY);
      currentY += 15;

      // Table Configuration
      const headers = [
        ["Employee", "Email", "Date", "Description", "Charges", "Status", "Approver", "Approved"],
      ];

      const tableData = data.map((item) => [
        item.employeeName || "-",
        item.email?.substring(0, 20) + (item.email?.length > 20 ? "..." : "") || "-",
        moment(item.date).format("MMM DD, YYYY"),
        item.description?.substring(0, 25) + (item.description?.length > 25 ? "..." : "") || "-",
        `$${parseFloat(item.charges || 0).toFixed(2)}`,
        getStatusText(item.status),
        item.approver || "-",
        item.approvedDate ? moment(item.approvedDate).format("MMM DD, YYYY") : "N/A",
      ]);

      // Generate Table
      doc.autoTable({
        startY: currentY,
        head: headers,
        body: tableData,
        theme: "grid",
        styles: {
          fontSize: 8,
          cellPadding: 2,
          lineColor: [200, 200, 200],
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [70, 130, 180],
          textColor: 255,
          fontStyle: "bold",
          fontSize: 9,
        },
        alternateRowStyles: {
          fillColor: [248, 248, 248],
        },
        columnStyles: {
          0: { cellWidth: 22, fontStyle: "bold" },
          1: { cellWidth: 25 },
          2: { cellWidth: 16 },
          3: { cellWidth: 30 },
          4: { cellWidth: 17, halign: "right" },
          5: { cellWidth: 18 },
          6: { cellWidth: 22 },
          7: { cellWidth: 20 },
        },
        margin: { left: 10, right: 10 },
        pageBreak: "auto",
      });

      // Calculate totals
      const finalY = doc.lastAutoTable.finalY + 10;
      const totalCharges = data.reduce((sum, item) => sum + (parseFloat(item.charges) || 0), 0);
      const approvedCharges = data
        .filter((item) => item.status === 2)
        .reduce((sum, item) => sum + (parseFloat(item.charges) || 0), 0);

      // Add summary
      doc.setFontSize(10);
      doc.setFont(undefined, "bold");
      doc.setTextColor(33, 37, 41);

      doc.text(`Total Charges: $${totalCharges.toFixed(2)}`, 14, finalY);
      doc.text(`Approved Charges: $${approvedCharges.toFixed(2)}`, 14, finalY + 6);
      doc.text(`Pending Charges: $${(totalCharges - approvedCharges).toFixed(2)}`, 14, finalY + 12);

      // Add page numbers
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${pageCount}`, 200, 290, { align: "right" });
      }

      // Generate filename and save
      const filename = `Expense-Report-${departmentName.replace(/\s+/g, "-")}-${
        new Date().toISOString().split("T")[0]
      }.pdf`;
      doc.save(filename);

      message.success("PDF report downloaded successfully!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      message.error("Failed to generate PDF report");
    }
  };

  // Helper function for status text
  const getStatusText = (status) => {
    switch (status) {
      case 0:
        return "Pending";
      case 1:
        return "In Process";
      case 2:
        return "Approved";
      case 3:
        return "Rejected";
      default:
        return "Unknown";
    }
  };

  const columns = [
    { title: "Employee Name", dataIndex: "employeeName", key: "employeeName" },
    { title: "Email", dataIndex: "email", key: "email" },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date) => moment(date).format("MMM-DD-yyyy"),
    },
    { title: "Description", dataIndex: "description", key: "description" },
    {
      title: "Charges",
      dataIndex: "charges",
      key: "charges",
      render: (v) =>
        v === null || v === undefined || v === ""
          ? "-"
          : `USD ${Number(v).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`,
    },
    {
      title: "Receipt",
      dataIndex: "receipt",
      key: "receipt",
      render: (url) =>
        url ? (
          <FileOpenOutlined style={{ cursor: "pointer" }} onClick={() => handlePdfPreview(url)} />
        ) : (
          "N/A"
        ),
    },
    {
      title: "Expense Sheet",
      dataIndex: "calculationSheet",
      key: "calculationSheet",
      render: (url) =>
        url ? (
          <FileOpenOutlined style={{ cursor: "pointer" }} onClick={() => handlePdfPreview(url)} />
        ) : (
          "N/A"
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) =>
        status === 0
          ? "Pending"
          : status === 1
          ? "InProcess"
          : status === 2
          ? "Approved"
          : "Rejected",
    },
    {
      title: "Approved Date",
      dataIndex: "approvedDate",
      key: "approvedDate",
      render: (date) => (date ? moment(date).format("MMM-DD-yyyy") : "N/A"),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        className="card-professional"
        title="Employee Expense  Report"
        extra={<Button onClick={() => window.history.back()}>Back</Button>}
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Space size="middle">
              <Select
                value={selectedDepartment}
                style={{ width: 200 }}
                onChange={setSelectedDepartment}
              >
                <Option value="">All Departments</Option>
                {departments.map((d) => (
                  <Option key={d.id} value={d.id}>
                    {d.name}
                  </Option>
                ))}
                {/* Executives: backend filters by Executive designation on -1. */}
                <Option value={-1}>Executives</Option>
              </Select>

              <Select value={periodType} onChange={setPeriodType} style={{ width: 140 }}>
                <Option value="">All Time</Option>
                {ReportPeriodTypeOptions.map((o) => (
                  <Option key={o.value} value={o.value}>
                    {o.label}
                  </Option>
                ))}
              </Select>

              {periodType !== "" && (
                <DatePicker
                  format="YYYY-MM-DD"
                  placeholder="Reference date (today)"
                  onChange={(d, ds) => setReferenceDate(ds || null)}
                />
              )}

              {periodLabel && <span style={{ color: "#666" }}>Period: {periodLabel}</span>}
            </Space>
          </Col>

          <Col>
            <Button
              className="btn-primary"
              icon={<FileDownloadOutlined />}
              onClick={handleDownloadPDF}
            >
              Download PDF
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="expenseManagementId"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>
      <Footer />
    </DashboardLayout>
  );
}

export default EmployeeExpenseRequest;
